<script setup>
import { computed } from 'vue'

// AbuseIPDB confidence-score cell. Sideloaded like observations, so it renders a
// spinner while `pending` and grades the 0–100 score by severity.
const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  pending: {
    type: Boolean,
    default: false
  }
})

const score = computed(() => {
  const s = props.data?.abuse_confidence_score
  return typeof s === 'number' ? s : null
})

// 0 grey · 1–24 low · 25–74 medium · 75–100 high
const severity = computed(() => {
  const s = score.value
  if (s == null) return null
  if (s === 0) return 'none'
  if (s < 25) return 'low'
  if (s < 75) return 'medium'
  return 'high'
})
</script>

<template>
  <div v-if="pending" class="abuse-pending" aria-label="Loading abuse score">
    <span class="abuse-spinner"></span>
  </div>
  <span
    v-else-if="score !== null"
    class="abuse-score"
    :class="`sev-${severity}`"
    :title="`AbuseIPDB confidence: ${score}%`"
  >
    {{ score }}<span class="abuse-score-unit">%</span>
  </span>
</template>

<style scoped>
.abuse-score {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.abuse-score-unit {
  font-weight: 400;
  opacity: 0.6;
  margin-left: 1px;
}

.abuse-score.sev-none {
  color: var(--text-muted);
}

.abuse-score.sev-low {
  color: var(--color-success-text);
}

.abuse-score.sev-medium {
  color: var(--color-warning-text);
}

.abuse-score.sev-high {
  color: var(--color-error-text);
}

.abuse-pending {
  display: inline-flex;
  align-items: center;
}

.abuse-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--surface-control);
  border-top-color: var(--accent);
  border-radius: var(--radius-pill);
  animation: abuse-score-spin 1s linear infinite;
}

@keyframes abuse-score-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
